<!--
  @component ScopePickerFeed — "pick a project" for a section that needs one.
  tier:   deployments view (shown when a per-project section is opened with no
          single project resolved — i.e. scope is all/many/none).

  Picking a project sets scope = {that one} and re-renders the section for it.
  (Phase 1 replaces this with real aggregated views for all/many.)
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useScopeIO } from "./ScopeIO.svelte";
  import { initial, projectGradient } from "../../home/home-util.js";

  let { section }: { section: string } = $props();

  const identity = useIdentityIO();
  const scope = useScopeIO();

  // The set to choose from: the current multi-select, or everything when
  // scope is "all" / empty.
  const candidates = $derived(
    !scope.isAll && scope.count > 0
      ? identity.projects.filter((p) => scope.has(p.id))
      : identity.projects,
  );

  const SECTION_NOUN: Record<string, string> = {
    overview: "overview",
    releases: "releases",
    code: "code",
    domain: "domain",
    data: "data",
    performance: "performance",
    logs: "logs",
  };

  // Selecting a project sets scope = {it}, which navigates to this section
  // with ?p=<id> (ScopeIO owns the URL write).
  function open(id: string): void {
    scope.setProjects([id]);
  }
</script>

<div class="feed">
  <header>
    <h2>Select a project</h2>
    <p>Choose a project to view its {SECTION_NOUN[section] ?? section}.</p>
  </header>

  {#if candidates.length === 0}
    <p class="empty">No projects yet.</p>
  {:else}
    <div class="grid">
      {#each candidates as p (p.id)}
        <button type="button" class="card" onclick={() => open(p.id)}>
          <span class="cover" style="background: {projectGradient(p.id)}">{initial(p.name)}</span>
          <span class="name" title={p.name}>{p.name}</span>
        </button>
      {/each}
    </div>
  {/if}
</div>

<style>
  .feed { display: flex; flex-direction: column; gap: var(--space-5); font-family: var(--type-font); max-width: 1100px; }
  header { display: flex; flex-direction: column; gap: var(--space-1); }
  h2 { margin: 0; font-size: 18px; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
  header p { margin: 0; font-size: 13.5px; color: var(--text-secondary); }
  .empty { margin: 0; font-size: 13px; color: var(--text-tertiary); }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: var(--space-3); }
  .card {
    display: flex; flex-direction: column; gap: var(--space-2);
    padding: var(--space-3); background: var(--glass-fill);
    border: 0.5px solid var(--border); border-radius: var(--radius-lg, 14px);
    cursor: pointer; text-align: left; font: inherit;
    transition: border-color var(--ease-fast), background var(--ease-fast);
  }
  .card:hover { border-color: var(--border-focus); background: var(--bg-hover); }
  .cover {
    height: 64px; border-radius: var(--radius-md); display: grid; place-items: center;
    font-size: 22px; font-weight: 600; color: rgba(255,255,255,0.92);
  }
  .name {
    font-size: 13.5px; font-weight: 500; color: var(--text-primary);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
</style>
